import { fetchEventSource } from "@microsoft/fetch-event-source";
import { fetchNewAccessToken } from "api/baseQuery";
import { resetAuth, setAuthTokens } from "features/authSlice";
import { AppDispatch } from "store";

interface FetchEventSourceOptions {
  url: string;
  token: string;
  refreshToken: string | null;
  dispatch: AppDispatch;
  onMessage: (data: any) => void;
  onError?: (error: any) => void;
  onDone?: () => void;
}

export class SSEManagerClass {
  private static instance: SSEManagerClass | null = null;
  private abortController: AbortController | null = null;
  public isConnected = false;
  private currentUrl: string | null = null;

  static getInstance(): SSEManagerClass {
    if (!this.instance) {
      this.instance = new SSEManagerClass();
    }
    return this.instance;
  }

  connect(options: FetchEventSourceOptions) {
    const { url, token, refreshToken, onMessage, dispatch, onError, onDone } =
      options;

    if (this.isConnected && this.currentUrl === url) {
      console.warn("SSE connection already established.");
      return;
    }
    this.close();
    this.isConnected = true;
    this.currentUrl = url;

    this.abortController = new AbortController();

    const startConnection = async (accessToken: string) => {
      try {
        await fetchEventSource(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: this.abortController!.signal,
          onopen: async (response) => {
            const handleTokenRefresh = async () => {
              const currentRefreshToken = refreshToken;
              if (!currentRefreshToken) {
                console.error("No refresh token available. Resetting auth.");
                dispatch(resetAuth());
                this.close();
                return;
              }
              const newTokens = await fetchNewAccessToken(currentRefreshToken);
              if (newTokens?.access_token) {
                const {
                  access_token: newAccessToken,
                  refresh_token: newRefreshToken,
                } = newTokens;
                dispatch(
                  setAuthTokens({
                    accessToken: newAccessToken,
                    refreshToken: newRefreshToken,
                  }),
                );
                console.log("Success Refrech Token");

                startConnection(newAccessToken).catch(console.error);
              } else {
                console.error("Failed to refresh token. Resetting auth.");
                dispatch(resetAuth());
                this.close();
              }
            };
            if (response.ok) {
              this.isConnected = true;
            } else if (response.status === 401 && refreshToken) {
              handleTokenRefresh().catch(console.error);
              console.log("401 SSE");
            }
          },
          onmessage: (event) => {
            try {
              if (!event.event) return;
              const validJsonStr = event.data;
              const data = JSON.parse(validJsonStr);

              localStorage.setItem("last_notification_id", event.id);

              onMessage({ data, event: event.event });
            } catch (_) {}
          },
          onerror: (error) => {
            console.error("SSE connection error:", error);
            this.close();
            if (onError) onError(error);
          },
          onclose: () => {
            console.log("SSE connection closed.");
            this.isConnected = false;
            if (onDone) onDone();
          },
        });
      } catch (error) {
        console.error("Failed to connect SSE:", error);
        if (onError) onError(error);
        this.close();
      }
    };
    startConnection(token).then();
  }
  close() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.isConnected = false;
    console.log("SSE connection terminated.");
  }
}
